import { __assign } from 'tslib';
import { fadeIn } from '../animations.js';
import { fonts } from '../typography.js';
import { iconButtons } from './icon-buttons.js';
import { buttons } from './buttons.js';
import { menuList } from './menu.js';

var _a, _b;
var datePickerLabelCss = {
    display: "flex",
    margin: "0 auto",
    position: "relative",
    height: "var(--button-size)",
    width: "var(--button-size)",
    flex: "0 0 var(--button-size)",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "100%",
    transition: "background-color ease-out, border-color ease-out",
    transitionDuration: 4,
    border: "2px solid transparent",
};
var datePicker = {
    base: {
        width: "fit-content",
        "[data-date-picker-body]": {
            position: "relative",
        },
        "[data-date-picker-header]": {
            position: "relative",
        },
        "[data-date-picker-month-table-container]": {
            position: "relative",
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "flex-start",
            gap: 7,
        },
        "[data-date-picker-year-list-container]": {
            position: "absolute",
            height: "100%",
            width: "100%",
            backgroundColor: "white",
            top: 0,
            left: 0,
            zIndex: "popover",
            animationName: fadeIn,
            animationDuration: 4,
        },
    },
};
var datePickerMonthTable = {
    base: {
        borderCollapse: "separate",
        borderSpacing: "0 4px",
        mb: "-4px",
    },
};
var datePickerMonthTh = {
    base: {
        height: "var(--button-size)",
        color: "black.80",
        textAlign: "center",
        verticalAlign: "middle",
        border: "none",
        py: 0,
        px: "2px",
        "[data-date-picker-label]": {
            textDecoration: "none",
        },
        width: "calc(100% / 7)",
    },
    variants: {
        size: {
            sm: {
                "--button-size": "24px",
                "[data-date-picker-label]": {
                    fontSize: "10px",
                },
            },
            md: {
                "--button-size": "32px",
                "[data-date-picker-label]": {
                    fontSize: "12px",
                },
            },
            lg: {
                "--button-size": "48px",
                "[data-date-picker-label]": {
                    fontSize: "14px",
                },
            },
        },
    },
    defaultProps: { size: "md" },
    variantOrder: ["size"],
};
var datePickerMonthTd = {
    base: __assign(__assign({}, datePickerMonthTh.base), { px: 0, color: "black.100", cursor: "pointer", position: "relative", ":focus": { outline: "none" }, ":after, :before": {
            content: "''",
            display: "block",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            opacity: 0.1,
            zIndex: "base",
            transition: "background-color ease-out, border-radius ease-out",
            transitionDuration: 4,
        }, "[data-date-picker-label]": datePickerLabelCss, "&[data-disabled]": { opacity: 0.3, cursor: "default" }, "&[data-readonly]": { cursor: "default" }, "&[data-is-different-month]": { opacity: 0, cursor: "default" }, "&[data-selected]": {
            "[data-date-picker-label]": {
                color: "white",
                backgroundColor: "primary.100",
            },
            ":focus&[data-is-focus]:not([data-disabled], [data-readonly], [data-different-month])": {
                "[data-date-picker-label]": {
                    boxShadow: 0,
                },
            },
        }, ":focus&[data-is-focus]:not([data-selected], [data-disabled], [data-readonly], [data-different-month])": {
            "[data-date-picker-label]": {
                backgroundColor: "primary.20",
            },
        }, ":hover:not([data-selected], [data-disabled], [data-readonly], [data-different-month])": {
            "[data-date-picker-label]": {
                borderColor: "primary.100",
                backgroundColor: "white",
            },
        }, "&[data-is-hover-range-start]": {
            ":before": {
                backgroundColor: "primary.100",
                width: "50%",
                left: "50%",
            },
        }, "&[data-is-hover-range-end]": {
            ":before": {
                backgroundColor: "primary.100",
                width: "50%",
            },
        }, "&[data-is-hover-range-between]": {
            ":before": {
                backgroundColor: "primary.100",
            },
        }, "&[data-is-range-start]": {
            ":after": {
                backgroundColor: "primary.100",
                width: "calc( 50% + var(--button-size)/2 )",
                left: "calc( 50% - var(--button-size)/2 )",
                borderTopLeftRadius: "var(--button-size)",
                borderBottomLeftRadius: "var(--button-size)",
            },
        }, "&[data-is-range-end]": {
            ":after": {
                backgroundColor: "primary.100",
                borderTopRightRadius: "var(--button-size)",
                borderBottomRightRadius: "var(--button-size)",
                width: "calc( 50% + var(--button-size)/2 )",
            },
        }, "&[data-is-range-between]": {
            ":after": {
                backgroundColor: "primary.100",
            },
        } }),
    variants: {
        size: __assign({}, (_a = datePickerMonthTh.variants) === null || _a === void 0 ? void 0 : _a.size),
    },
    defaultProps: { size: "md" },
    variantOrder: ["size"],
};
var datePickerYearList = {
    base: {
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        columnGap: 2,
        rowGap: 1,
    },
};
var datePickerYearItem = {
    base: __assign(__assign({}, datePickerMonthTd.base), { width: "auto", "[data-date-picker-label]": __assign(__assign({}, datePickerLabelCss), { width: "100%", borderRadius: "var(--button-size)" }) }),
    variants: {
        size: __assign({}, (_b = datePickerMonthTh.variants) === null || _b === void 0 ? void 0 : _b.size),
    },
    defaultProps: { size: "md" },
    variantOrder: ["size"],
};
var datePickerArrowButton = {
    base: __assign(__assign({}, iconButtons.base), { color: "black.100", borderRadius: "100%", backgroundColor: "transparent", border: "2px solid transparent", transition: "background-color ease-out, border-color ease-out", transitionDuration: 4, position: "absolute", top: "50%", transform: "translateY(-50%)", right: 0, ":focus:not(:disabled):not([data-disabled='true'])": {
            // borderColor: "primary.100",
            backgroundColor: "primary.20",
            outline: "none",
        }, ":hover:not(:disabled):not([data-disabled='true'])": {
            borderColor: "primary.100",
        }, "&[data-direction='left']": {
            transform: "translateY(-50%) scaleX(-1)",
            right: "auto",
            left: 0,
        }, ":disabled": {
            opacity: 0.3,
        } }),
    variants: {
        size: {
            sm: {
                "--button-size": "24px",
                fontSize: "12px",
            },
            md: {
                "--button-size": "32px",
                fontSize: "16px",
            },
            lg: {
                "--button-size": "48px",
                fontSize: "20px",
            },
        },
    },
    defaultProps: { size: "md" },
    variantOrder: ["size"],
};
var datePickerModeToggleButton = __assign(__assign({}, buttons), { base: __assign(__assign({}, buttons.base), { height: "auto", width: "100%", justifySelf: "center", backgroundColor: "transparent", borderColor: "transparent" }) });
var datePickerHeading = {
    base: __assign(__assign({}, fonts.body), { fontSize: "inherit", fontWeight: "bold", color: "inherit", textAlign: "center", flex: 1, mx: 0, my: 2, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(1%, 1fr))", alignItems: "center", minWidth: "100%", columnGap: 6, justifyContent: "center", ">*": { verticalAlign: "middle", flex: 1 } }),
    variants: __assign(__assign({}, buttons.variants), { size: {
            sm: {
                fontSize: "12px",
            },
            md: {
                fontSize: "14px",
            },
            lg: {
                fontSize: "16px",
            },
        } }),
    defaultProps: { size: "md" },
};
var datePickerMenuContent = {
    base: {
        width: "fit-content",
    },
};
var datePickerMenuContentCard = __assign(__assign({}, menuList), { base: __assign(__assign(__assign({}, menuList.base), fonts.body), { px: 6, pb: 6, pt: 5, width: "fit-content" }) });
var datePickerRangeGrid = {
    base: {
        gridTemplateColumns: "1fr 1fr",
        gap: 3,
        alignItems: "start",
    },
};

export { datePicker, datePickerArrowButton, datePickerHeading, datePickerMenuContent, datePickerMenuContentCard, datePickerModeToggleButton, datePickerMonthTable, datePickerMonthTd, datePickerMonthTh, datePickerRangeGrid, datePickerYearItem, datePickerYearList };
//# sourceMappingURL=date-picker.js.map
